import { DraftAttachmentDoc } from "libs/schema";
import { toast } from "~/environment/toast-service";
import * as actions from "libs/actions";
import { debouncedWrite, withActionContext, write } from "./write";
import { EnqueueOptions } from "~/environment/TransactionQueue";
import { getDraftDebounceKey } from "./draft";
import { reindexNotification } from "./notification";
import { DraftRecipient, EditorMention } from "libs/actions/draft";

/* -------------------------------------------------------------------------------------------------
 *  createEditDraft
 * -------------------------------------------------------------------------------------------------
 */

export interface CreateEditDraftProps {
  messageId: string;
  ownerOrganizationId: string;
  threadId: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  bodyHTML?: string;
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
}

/**
 * Updates a draft. If the draft is ready to be sent (scheduled_to_be_sent_at is set), it will be sent.
 */
export const createEditDraft = withActionContext(async (context, environment, props: CreateEditDraftProps) => {
  return {
    forward: async () => {
      // If the message being edited was just optimistically created via a draft, it's possible
      // that there are still debounced writes associated with the draft. We wait for these
      // to be flushed.
      await debouncedWrite.flush([getDraftDebounceKey(props.messageId)]);

      const action = await actions.draftEdit.createEditDraft.action(props, context);

      await write(environment, { action });
    },
  };
});

/* -------------------------------------------------------------------------------------------------
 *  setDraftEdit
 * -------------------------------------------------------------------------------------------------
 */

export interface SetDraftEditProps {
  draftId: string;
  ownerOrganizationId: string;
  threadId: string;
  bodyHTML?: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
  noDebounce?: boolean;
}

/**
 * This is intended to be used for updates, but since it's implemented as a "set" I decided to name it that.
 * Prefer "createDraft" for creating a new draft. This function should not be used for "undeleting" a draft.
 * We attempt to guard against accidental undeletes by checking if the draft has been deleted before updating it.
 */
export const setDraftEdit = withActionContext(async (context, environment, props: SetDraftEditProps) => {
  return {
    forward: async () => {
      const debounceOptions: EnqueueOptions["debounce"] | undefined =
        props.noDebounce ? undefined : (
          { key: getDraftEditDebounceKey(props.draftId), debounceMs: 1500, maxWaitMs: 4000 }
        );

      await debouncedWrite(environment, debounceOptions, async () => {
        const action = await actions.draftEdit.setDraftEdit.action(props, context);

        return { action };
      });
    },
  };
});

/* -------------------------------------------------------------------------------------------------
 *  commitDraftEdits
 * -------------------------------------------------------------------------------------------------
 */

export const commitDraftEdits = withActionContext(
  async (
    context,
    environment,
    props: SetDraftEditProps & {
      shouldResendNotifications?: boolean;
      noToast?: boolean;
    },
  ) => {
    return {
      forward: async () => {
        // Wait for any debounced writes associated with the draft to finish being added to the tx queue.
        await debouncedWrite.flush([getDraftEditDebounceKey(props.draftId)]);

        const action = await actions.draftEdit.commitDraftEdits.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            reindexNotification(environment, { threadId: props.threadId });
          },
        });
      },
      // If the edits added recipients to the thread, and if those changes were processed
      // by the server, then undoing them is more complicated than just undoing this tx.
      // backward: async () => {}
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 *  deleteDraftEdit
 * -------------------------------------------------------------------------------------------------
 */

export const deleteDraftEdit = withActionContext(
  async (
    context,
    environment,
    props: {
      draftId: string;
      afterUndo?: () => void;
    },
  ) => {
    return {
      forward: async () => {
        environment.logger.info({ draftId: props.draftId }, `[deleteDraftEdit] deleting draft edit ...`);

        // Wait for any debounced writes associated with the draft to finish being added to the tx queue.
        await debouncedWrite.flush([getDraftEditDebounceKey(props.draftId)]);

        const action = await actions.draftEdit.deleteDraftEdit.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: "Edits discarded.",
            });
          },
        });
      },
      backward: async () => {
        environment.logger.info({ draftId: props.draftId }, `[deleteDraftEdit] resorting draft edit ...`);

        const action = await actions.draftEdit.unDeleteDraftEdit.action(props, context);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            toast("vanilla", {
              subject: "Edits restored.",
            });
          },
        });
      },
    };
  },
);

/* -----------------------------------------------------------------------------------------------*/

export function getDraftEditDebounceKey(draftId: string) {
  return `draft-edit-${draftId}`;
}

/* -----------------------------------------------------------------------------------------------*/
